import { Phone, Truck } from 'lucide-react'
import { BoxTruckMailingService } from '@numeo/types-main-web-app'
import { Badge, Button, Checkbox } from '@/components/ds2'
import { BoxTruckVerification } from '../services/boxTruckLoadsApi'
import { hasTelegram, hasTextablePhone, isMailingReachable } from '../utils/driverReachability'

/** The Mailing recipient picker: one row per claimed driver with contact/status/note columns, BOTH
 *  services' reachability, a header select-all over the REACHABLE rows, and (Telegram) a Connect action
 *  for unconnected ones. Presentational — selection state lives in the page's form. */
export const MailingDriversTable = ({
    drivers,
    service,
    selectedIds,
    onToggleDriver,
    onToggleAll,
    allSelected,
    onConnect,
}: {
    drivers: BoxTruckVerification[]
    /** Which service the mailing goes out on — decides which rows are selectable. */
    service: BoxTruckMailingService
    selectedIds: string[]
    onToggleDriver: (userId: string) => void
    /** Header checkbox: every reachable driver in / out. */
    onToggleAll: () => void
    allSelected: boolean
    onConnect: (driver: BoxTruckVerification) => void
}) => {
    if (drivers.length === 0) return <p className="text-xs text-foreground-muted">No drivers yet — add some in Profiles → Drivers first.</p>
    const reachableCount = drivers.filter((d) => isMailingReachable(d, service)).length
    const sms = service === 'sms'
    return (
        <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left text-sm">
                <thead>
                    <tr className="border-b border-border bg-default/40 text-xs text-foreground-muted">
                        <th className="w-9 px-3 py-2">
                            <Checkbox checked={allSelected} disabled={reachableCount === 0} onChange={onToggleAll} ariaLabel="Select all reachable drivers" />
                        </th>
                        <th className="px-2 py-2 font-medium">Driver</th>
                        <th className="px-2 py-2 font-medium">Status</th>
                        {/* BOTH services, always, with the one being sent on first. An operator composing an
                            SMS needs to see who is on Telegram — that is what makes "the drivers Telegram
                            cannot reach" a thing they can see rather than a thing they have to work out. */}
                        <th className="px-2 py-2 font-medium">{sms ? 'Phone' : 'Telegram'}</th>
                        <th className="px-2 py-2 font-medium text-foreground-muted/70">{sms ? 'Telegram' : 'Phone'}</th>
                        <th className="px-2 py-2 font-medium">Note</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {drivers.map((d) => {
                        const reachable = isMailingReachable(d, service)
                        const name = d.boxTruckInfo.info?.name || d.name || d.email
                        const info = d.boxTruckInfo.info
                        const truck = [info?.truckNumber && `#${info.truckNumber}`, info?.equipmentType].filter(Boolean).join(' · ')
                        const status = d.boxTruckInfo.status
                        const availability = d.availability?.status
                        const note = d.teamNote?.text
                        const selected = selectedIds.includes(d.userId)
                        return (
                            <tr
                                key={d.userId}
                                onClick={() => reachable && onToggleDriver(d.userId)}
                                className={`transition-colors ${reachable ? `cursor-pointer ${selected ? 'bg-accent-soft/30' : 'hover:bg-default/40'}` : 'opacity-80'}`}
                            >
                                <td className="px-3 py-2.5 align-top" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox checked={selected} disabled={!reachable} onChange={() => onToggleDriver(d.userId)} ariaLabel={`Select ${name}`} />
                                </td>
                                <td className="px-2 py-2.5 align-top">
                                    <div className="max-w-[180px] truncate font-medium">{name}</div>
                                    <div className="mt-0.5 flex flex-col gap-0.5 text-xs text-foreground-muted">
                                        {info?.phone && (
                                            <span className="inline-flex items-center gap-1">
                                                <Phone size={11} /> {info.phone}
                                            </span>
                                        )}
                                        {truck && (
                                            <span className="inline-flex items-center gap-1">
                                                <Truck size={11} /> {truck}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-2 py-2.5 align-top">
                                    <div className="flex flex-col items-start gap-1">
                                        {status && (
                                            <Badge
                                                variant="soft"
                                                type={status === 'active' ? 'success' : status === 'pending' ? 'warning' : 'danger'}
                                                label={status === 'active' ? 'Active' : status === 'pending' ? 'Pending' : 'Inactive'}
                                            />
                                        )}
                                        {availability && (
                                            <Badge
                                                variant="soft"
                                                type={availability === 'available' ? 'success' : availability === 'booked' ? 'accent' : 'warning'}
                                                label={availability === 'available' ? 'Available' : availability === 'booked' ? 'Booked' : 'Unavailable'}
                                            />
                                        )}
                                    </div>
                                </td>
                                <td className="px-2 py-2.5 align-top">
                                    {sms ? (
                                        reachable ? (
                                            <Badge variant="soft" type="success" label="Has phone" />
                                        ) : (
                                            // The number lives on the driver's profile; the picker only reports that it is
                                            // missing or unusable, and which of the two it is.
                                            <Badge variant="soft" type="danger" label={info?.phone ? 'Not a US number' : 'No phone'} />
                                        )
                                    ) : reachable ? (
                                        <div>
                                            <Badge variant="soft" type="success" label="Connected" />
                                            <div className="mt-0.5 max-w-[140px] truncate text-xs text-foreground-muted">
                                                {d.telegramGroup?.groupName || ''}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Badge variant="soft" type="danger" label="Not connected" />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onConnect(d)
                                                }}
                                            >
                                                Connect
                                            </Button>
                                        </div>
                                    )}
                                </td>
                                {/* The OTHER service, muted on purpose: this is information about the driver, not a
                                    reason the row is disabled — the selectable column is the one before it. */}
                                <td className="px-2 py-2.5 align-top">
                                    {sms ? (
                                        hasTelegram(d) ? (
                                            <Badge variant="soft" type="default" label="On Telegram" />
                                        ) : (
                                            <span className="text-xs text-foreground-muted/60">Not on Telegram</span>
                                        )
                                    ) : hasTextablePhone(d) ? (
                                        <Badge variant="soft" type="default" label="Has phone" />
                                    ) : (
                                        <span className="text-xs text-foreground-muted/60">{info?.phone ? 'Not a US number' : 'No phone'}</span>
                                    )}
                                </td>
                                <td className="max-w-[160px] px-2 py-2.5 align-top">
                                    {note ? (
                                        <span className="line-clamp-2 text-xs text-foreground-muted" title={note}>
                                            {note}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-foreground-muted/50">—</span>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
